import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExportMenuItems,
  Flex,
  FormControlSizes,
  HedgeControlStatusEnum,
  IconName,
  WARNING_ROW_CLASSNAME,
  useAccordionFilterBuilder,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  usePersistedBlotterTable,
  type IHedgePositionStatus,
} from '@talos/kyoko';

import type { GridOptions } from 'ag-grid-community';
import { useCallback } from 'react';
import { SynchronizeHedgerButton } from '../../../components/AutoHedging/SynchronizeHedgerButton';
import { useNavigateToHedgeRulesSetup } from '../../../components/AutoHedging/useNavigateToHedgeRulesSetup';
import { usePositionAutoHedgingRulesDrawer } from '../../../components/AutoHedgingRulesDrawer/AutoHedgingRulesDrawer';
import { useHedgePositionStatus, type IHedgePositionStatusRow } from '../../../providers/HedgePositionStatusProvider';
import { useHedgePositionStatusColumns } from './columns';
import type { HedgePositionStatusBlotterTableProps } from './types';
import { useHedgePositionStatusFilter } from './useHedgePositionStatusFilter';
import { useHedgePositionStatusMenu } from './useHedgePositionStatusMenu';

export function HedgePositionStatusBlotterTable({ blotterID, tab }: HedgePositionStatusBlotterTableProps) {
  const defaultColumns = useHedgePositionStatusColumns({ size: 'full', defaultColumns: tab.defaultColumns });
  const { getContextMenuItems, modals } = useHedgePositionStatusMenu();
  const persisted = usePersistedBlotterTable(blotterID, {
    filter: tab.defaultFilter,
  });

  const positionAutoHedgingFilter = useHedgePositionStatusFilter({
    initialFilter: persisted.initialState?.filter,
    saveFilter: persisted.onFilterChanged,
  });
  const { clientSideFilter, filterableProperties, initialFilterClauses, handleFilterClausesChanged } =
    positionAutoHedgingFilter;

  const accordionFilterBuilder = useAccordionFilterBuilder({
    filterBuilderProps: {
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    },
  });

  const { hedgePositionStatusDeltasObs } = useHedgePositionStatus();
  const { openPositionAutoHedgingRulesDrawer } = usePositionAutoHedgingRulesDrawer();
  type HandleOnRowDoubleClicked = NonNullable<GridOptions<IHedgePositionStatusRow>['onRowDoubleClicked']>;
  const handleOnRowDoubleClicked = useCallback<HandleOnRowDoubleClicked>(
    ({ data }) => {
      if (data) {
        openPositionAutoHedgingRulesDrawer({ hedgeRuleID: data.HedgeRuleID });
      }
    },
    [openPositionAutoHedgingRulesDrawer]
  );

  const blotterTable = useBlotterTable<IHedgePositionStatus>({
    dataObservable: hedgePositionStatusDeltasObs,
    errorObservable: undefined,
    columns: defaultColumns,
    persistence: persisted,
    rowID: 'HedgeRuleID',
    clientLocalFilter: clientSideFilter,
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems,
      onRowDoubleClicked: handleOnRowDoubleClicked,
      rowClassRules: {
        [WARNING_ROW_CLASSNAME]: params => params.data?.HedgeControlStatus === HedgeControlStatusEnum.Error,
      },
    },
    exportOptions: {
      name: 'Position Autohedging',
    },
  });

  const handleClickOpenRules = useNavigateToHedgeRulesSetup();
  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <BlotterTableFilters
        {...accordionFilterBuilder}
        {...blotterTable.blotterTableFiltersProps}
        prefix={
          <Flex flexDirection="row" gap="spacing4">
            <SynchronizeHedgerButton />
            <Button onClick={handleClickOpenRules} startIcon={IconName.Preferences} size={FormControlSizes.Small}>
              Open Rules
            </Button>
          </Flex>
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {modals}
    </>
  );
}
